import { useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { DistanceSettings, CategorySettings } from '../components/SettingsModal';

export type TripGrouping = 'day' | 'location';
export type AiMode = 'off' | 'low' | 'medium' | 'high';
export type ThemeMode = 'light' | 'dark' | 'system';

interface UserSettings {
  distance_settings: DistanceSettings;
  category_settings: CategorySettings;
  show_open_only: boolean;
  min_rating: number;
  trip_grouping: TripGrouping;
  ai_mode: AiMode;
  theme_mode: ThemeMode;
  language?: string;
  referral_code?: string;
}

export function useSettings() {
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSettings = useCallback(async (userId: string): Promise<UserSettings | null> => {
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('user_settings')
      .select('distance_settings, category_settings, show_open_only, min_rating, trip_grouping, ai_mode, theme_mode, language, referral_code')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error loading settings:', error);
      return null;
    }

    return data;
  }, []);

  const saveSettings = useCallback(async (
    userId: string,
    settings: UserSettings
  ): Promise<boolean> => {
    if (!supabase) return false;
    const { error } = await supabase
      .from('user_settings')
      .upsert({
        user_id: userId,
        distance_settings: settings.distance_settings,
        category_settings: settings.category_settings,
        show_open_only: settings.show_open_only,
        min_rating: settings.min_rating,
        trip_grouping: settings.trip_grouping,
        ai_mode: settings.ai_mode,
        theme_mode: settings.theme_mode,
        language: settings.language || 'en',
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      console.error('Error saving settings:', error);
      return false;
    }

    return true;
  }, []);

  const debouncedSave = useCallback((
    userId: string,
    settings: UserSettings
  ) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveSettings(userId, settings);
    }, 500);
  }, [saveSettings]);

  return {
    loadSettings,
    saveSettings,
    debouncedSave
  };
}
